import { useState } from "react";
import { observer } from "mobx-react-lite";
import { Formik, Form } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { useTran } from "@/hooks";
import { User, Eye, EyeOff } from "lucide-react";
import { useAuthStore } from "../hooks";
import { LoginSchema } from "../schemas";
import { PATHS } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/common";

export const LoginForm = observer(() => {
  const navigate = useNavigate();
  const { t } = useTran();
  const [showPassword, setShowPassword] = useState(false);
  const { loginForm, loading, login } = useAuthStore();

  const handleLogin = async (values, { setSubmitting }) => {
    const success = await login(values);
    setSubmitting(false);

    if (success) {
      navigate(PATHS.DASHBOARD, { replace: true });
    }
  };

  return (
    <Formik
      initialValues={{ usernameOrEmail: "", password: "" }}
      validationSchema={LoginSchema}
      onSubmit={handleLogin}
    >
      {({ isSubmitting, errors, touched, values, handleChange }) => (
        <Form className="w-full space-y-3 px-8 md:px-12 lg:px-16">
          <h1 className="text-center text-3xl font-semibold text-foreground">
            {t("Login")}
          </h1>

          {/* Username or Email */}
          <FormField
            name="usernameOrEmail"
            placeholder={t("Username or Email")}
            value={values.usernameOrEmail}
            onChange={handleChange}
            error={touched.usernameOrEmail && errors.usernameOrEmail}
            rightIcon={<User className="h-5 w-5" />}
            className="space-y-1"
          />

          {/* Password */}
          <FormField
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder={t("Password")}
            value={values.password}
            onChange={handleChange}
            error={touched.password && errors.password}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            }
            className="space-y-1"
            rightIconClassName="top-[60%]"
          />

          {/* Server Error */}
          {loginForm?.serverError && (
            <p className="text-xs text-red-600">{loginForm.serverError}</p>
          )}

          {/* Forgot Password */}
          <div className="text-right">
            <Link
              to={PATHS.FORGOT_PASSWORD}
              className="text-sm text-primary hover:underline"
            >
              {t("Forgot Password")}
            </Link>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting || loading}
            className="w-full rounded-full bg-secondary hover:bg-secondary/90"
          >
            {isSubmitting || loading ? t("Logging in...") : t("Login")}
          </Button>
        </Form>
      )}
    </Formik>
  );
});
